import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { manhuaList } from '@/mocks/manhua';

export default function ManhuaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const manhua = manhuaList.find((m) => m.id === id);

  if (!manhua) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <p className="text-foreground-500">漫画不存在</p>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://youplz.com/' },
          { '@type': 'ListItem', position: 2, name: manhua.category, item: `http://youplz.com/category/${manhua.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: manhua.title, item: `http://youplz.com/manhua/${manhua.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: manhua.title,
        description: manhua.description,
        author: { '@type': 'Person', name: manhua.author },
        datePublished: manhua.chapters[manhua.chapters.length - 1]?.createdAt || manhua.updatedAt,
        dateModified: manhua.updatedAt,
        image: manhua.cover,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />
      <main>
        <section className="bg-background-100 pt-24 pb-10">
          <div className="px-4 md:px-8 lg:px-16">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${manhua.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{manhua.category}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium whitespace-nowrap">{manhua.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px] mx-auto lg:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manhua.cover}
                    alt={manhua.altText}
                    title={manhua.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {manhua.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight">
                  {manhua.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className="text-sm text-foreground-600 whitespace-nowrap">
                    <i className="ri-user-line mr-1"></i>{manhua.author}
                  </span>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">
                    <i className="ri-eye-line mr-1"></i>{(manhua.views / 10000).toFixed(0)}万阅读
                  </span>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < Math.floor(manhua.rating) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-secondary-300'}`}
                      ></i>
                    ))}
                    <span className="text-sm font-medium text-foreground-800 ml-1">{manhua.rating}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {manhua.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 bg-background-50 rounded-lg p-5">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-3 font-label">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{manhua.description}</p>
                </div>

                <div className="mt-6 flex gap-3">
                  <Link
                    to={`/category/${manhua.categorySlug}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    开始阅读
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to={`/category/${manhua.categorySlug}`}
                    className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 text-sm font-medium rounded-full hover:border-primary-500 hover:text-primary-500 transition-colors whitespace-nowrap"
                  >
                    加入收藏
                    <i className="ri-bookmark-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-10 md:py-14">
          <div className="px-4 md:px-8 lg:px-16 max-w-3xl">
            <h2 className="text-lg font-bold text-foreground-950 mb-5 font-heading">章节目录</h2>
            <div className="space-y-2">
              {manhua.chapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/category/${manhua.categorySlug}`}
                  className="flex items-center justify-between px-4 py-3 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors group"
                >
                  <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    第{ch.number}话 {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.createdAt}</span>
                </Link>
              ))}
            </div>
            <div className="mt-4 text-center">
              <span className="text-xs text-foreground-400">共 {manhua.chapters.length} 话（更多章节请访问分类页面查看）</span>
            </div>
          </div>
        </section>

        <section className="relative w-full py-20 md:py-28 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20warm%20atmospheric%20background%20with%20soft%20gradients%20in%20golden%20and%20rose%20tones%2C%20dreamy%20abstract%20light%20patterns%2C%20gentle%20bokeh%20effect%2C%20elegant%20minimalist%20composition%2C%20warm%20inviting%20aesthetic%2C%20reading%20mood%20vibe&width=1600&height=800&seq=cta-detail-bg&orientation=landscape"
            alt="继续阅读更多韩漫"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-foreground-950/35"></div>
          <div className="relative z-10 px-4 md:px-8 lg:px-16 max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-background-50 font-heading leading-tight">
                喜欢这部作品？
                <br />
                探索更多精彩韩漫
              </h2>
              <div className="mt-6">
                <Link
                  to={`/category/${manhua.categorySlug}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-950 text-sm font-medium rounded-full hover:bg-background-100 transition-all whitespace-nowrap"
                >
                  浏览{manhua.category}分类
                  <span className="w-5 h-5 rounded-full bg-foreground-950 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-background-50 text-[10px]"></i>
                  </span>
                </Link>
              </div>
            </div>
            <div>
              <p className="text-sm text-background-50/80 leading-relaxed max-w-xs">
                韩漫网收录海量{manhua.category}类韩漫，
                <br />
                下拉式阅读体验，随时随地畅享
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}